import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Play, Pause, Ban, RotateCcw, Download, ArrowLeft, CheckCircle2, XCircle, Clock, Send } from "lucide-react";
import CampaignResendDialog from "@/components/bulk/CampaignResendDialog";

const STATUS_STYLE = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-sun/25 text-yellow-700",
  queued: "bg-sun/25 text-yellow-700",
  running: "bg-emerald-brand/15 text-emerald-brand animate-pulse",
  paused: "bg-yellow-100 text-yellow-700",
  completed: "bg-emerald-brand/15 text-emerald-brand",
  cancelled: "bg-muted text-muted-foreground",
  error: "bg-crimson/12 text-crimson",
};

export default function CampaignDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [resendOpen, setResendOpen] = useState(false);
  const wsRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const loadCampaign = async () => {
    try { const { data } = await api.get(`/bulk/campaigns/${id}`); setC(data); }
    catch (e) { toast.error("Failed to load campaign"); }
  };
  const loadDeliveries = async () => {
    const params = { limit: 200 };
    if (statusFilter) params.status = statusFilter;
    if (filter) params.q = filter;
    const { data } = await api.get(`/bulk/campaigns/${id}/deliveries`, { params });
    setDeliveries(data.items);
    setTotal(data.total);
  };

  useEffect(() => { loadCampaign(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { loadDeliveries(); /* eslint-disable-next-line */ }, [id, filter, statusFilter]);

  // WebSocket live updates — subscribed to the shared notifications WS.
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const url = backendUrl.replace(/^http/, "ws") + `/api/ws/notifications?token=${token}`;
    let closed = false;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        // Server publishes {type:'campaign', campaign_id, status, ...}
        if (msg.type === "notification" && msg.data?.title?.includes("Campaign")) {
          loadCampaign();
        }
        if (msg.type === "campaign" && msg.campaign_id === id) {
          setC((prev) => prev ? { ...prev, sent: msg.sent ?? prev.sent, failed: msg.failed ?? prev.failed, total: msg.total ?? prev.total, status: msg.status?.replace("delivery", prev.status) === "delivery" ? prev.status : (msg.status || prev.status) } : prev);
          if (msg.status === "delivery" || msg.status === "completed" || msg.status === "running") {
            loadDeliveries();
          }
        }
      } catch { /* ignore */ }
    };
    ws.onclose = () => { if (!closed) setTimeout(() => { /* auto-reconnect handled elsewhere */ }, 3000); };
    return () => { closed = true; try { ws.close(); } catch { /* */ } };
  }, [id, backendUrl]);

  // Also poll every 4s as a safety net
  useEffect(() => {
    const t = setInterval(() => { loadCampaign(); loadDeliveries(); }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id, filter, statusFilter]);

  const doAction = async (act) => {
    try { await api.post(`/bulk/campaigns/${id}/${act}`); toast.success(`Campaign ${act}ed`); loadCampaign(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const exportCsv = () => {
    window.open(`${backendUrl}/api/bulk/campaigns/${id}/deliveries.csv?token=${localStorage.getItem("token")}`, "_blank");
  };

  const pct = useMemo(() => c?.total ? Math.round(((c.sent + c.failed) / c.total) * 100) : 0, [c]);

  if (!c) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const st = STATUS_STYLE[c.status] || STATUS_STYLE.draft;

  return (
    <div className="p-6 lg:p-8 space-y-4" data-testid="campaign-detail-page">
      <div className="flex items-center gap-2">
        <Link to="/bulk/campaigns"><Button variant="ghost" size="sm" className="rounded-sm"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> All campaigns</Button></Link>
      </div>

      <div className="rounded-2xl border border-border bg-white p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl font-semibold tracking-tight">{c.name}</h2>
              <Badge className={`rounded-sm text-[10px] font-mono uppercase ${st}`} data-testid="campaign-status">{c.status}</Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Subject: <span className="font-mono text-foreground">{c.subject}</span>
            </div>
            <div className="text-[11px] font-mono text-muted-foreground mt-1">
              From {c.from_name ? `${c.from_name} · ` : ""}{c.from_email} · throttle {c.throttle_per_hour}/hr
              {c.schedule_at ? ` · scheduled ${c.schedule_at.slice(0, 16).replace("T", " ")}` : ""}
            </div>
          </div>
          <div className="flex gap-1 flex-wrap">
            {["draft", "scheduled", "paused"].includes(c.status) && (
              <Button size="sm" onClick={() => doAction("start")} className="rounded-sm" data-testid="start-btn"><Play className="h-3.5 w-3.5 mr-1" /> Start</Button>
            )}
            {c.status === "running" && (
              <Button size="sm" variant="outline" onClick={() => doAction("pause")} className="rounded-sm" data-testid="pause-btn"><Pause className="h-3.5 w-3.5 mr-1" /> Pause</Button>
            )}
            {["running", "queued", "paused", "scheduled"].includes(c.status) && (
              <Button size="sm" variant="outline" onClick={() => doAction("cancel")} className="rounded-sm" data-testid="cancel-btn"><Ban className="h-3.5 w-3.5 mr-1" /> Cancel</Button>
            )}
            {c.status === "completed" && (
              <Button size="sm" variant="outline" onClick={() => setResendOpen(true)} className="rounded-sm" data-testid="resend-btn"><RotateCcw className="h-3.5 w-3.5 mr-1" /> Resend</Button>
            )}
            <Button size="sm" variant="outline" onClick={exportCsv} className="rounded-sm" data-testid="export-csv-btn"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <Stat label="Total" value={c.total} icon={Send} tone="slate" testid="stat-total" />
          <Stat label="Sent" value={c.sent} icon={CheckCircle2} tone="emerald" testid="stat-sent" />
          <Stat label="Failed" value={c.failed} icon={XCircle} tone="crimson" testid="stat-failed" />
          <Stat label="Suppressed" value={c.suppressed || 0} icon={Ban} tone="sun" testid="stat-suppressed" />
          <Stat label="Pending" value={Math.max((c.total || 0) - (c.sent || 0) - (c.failed || 0) - (c.suppressed || 0), 0)} icon={Clock} tone="sun" testid="stat-pending" />
        </div>

        <div>
          <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
            <span>Progress</span><span>{pct}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-brand to-sun transition-[width] duration-300" style={{ width: `${pct}%` }} data-testid="progress-bar" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-white">
        <div className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Deliveries ({total})</span>
          <Input placeholder="Search email…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 w-56 rounded-sm" data-testid="delivery-search" />
          <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger className="h-8 w-40 rounded-sm text-xs" data-testid="delivery-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="queued">Queued</SelectItem>
              <SelectItem value="suppressed">Suppressed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="tbl w-full" data-testid="deliveries-table">
            <thead className="sticky top-0 bg-white z-10"><tr><th>Recipient</th><th>Status</th><th>Sent at</th><th>Message ID</th><th>Error</th></tr></thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id} data-testid={`delivery-row-${d.id}`}>
                  <td>
                    <div className="font-medium">{d.recipient_email}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">{d.recipient_name || "—"}{d.custom_data?.company ? ` · ${d.custom_data.company}` : ""}</div>
                  </td>
                  <td>
                    <Badge className={`rounded-sm text-[10px] font-mono uppercase ${d.status === "sent" ? "bg-emerald-brand/15 text-emerald-brand" : d.status === "failed" ? "bg-crimson/12 text-crimson" : d.status === "suppressed" ? "bg-sun/25 text-yellow-800" : "bg-sun/25 text-yellow-700"}`}>{d.status}</Badge>
                  </td>
                  <td className="font-mono text-[10px] text-muted-foreground">{d.sent_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                  <td className="font-mono text-[10px] text-muted-foreground truncate max-w-[180px]" title={d.message_id}>{d.message_id?.slice(0, 24) || "—"}</td>
                  <td className="text-[11px] text-crimson max-w-[240px] truncate" title={d.error_message}>{d.error_message || ""}</td>
                </tr>
              ))}
              {deliveries.length === 0 && <tr><td colSpan="5" className="text-center text-muted-foreground py-6">No deliveries yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {resendOpen && <CampaignResendDialog campaign={c} onClose={() => setResendOpen(false)} onDone={() => { setResendOpen(false); loadCampaign(); }} />}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone, testid }) {
  const tones = { emerald: "text-emerald-brand", sun: "text-yellow-700", crimson: "text-crimson", slate: "text-muted-foreground" };
  return (
    <div className="rounded-xl border border-border p-3 flex items-center justify-between" data-testid={testid}>
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`font-display text-2xl font-semibold ${tones[tone]}`}>{value ?? 0}</div>
      </div>
      <Icon className={`h-5 w-5 ${tones[tone]}`} strokeWidth={1.5} />
    </div>
  );
}
